'use client'

import * as m from 'motion/react-m'
import { Motion } from '@/lib/motion/lazy-motion'
import Hero from '@/components/content/common/Hero'
import Container from '@/components/structure/Container'
import AppStack from '@/components/content/app/AppStack'
import Meta from '@/components/structure/Meta'
import { Icon } from '@/icons/Icon'
import { hexToRgba, getBrandColors } from '@/lib/utils'
import { checkAppBranding } from '@/lib/formatting'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import type { App, DealInfo } from '@/payload-types'

type DealEmptyStateProps = {
  app: App
  dealInfo: DealInfo
}

// Rendered when no active published Deal exists for an app but a DealInfo
// record does. Keeps the URL indexable so a temporarily-pulled vendor offer
// doesn't get deindexed by Google. See EA-720.
//
// Intentionally hero-only — deal_info.tldr / details / faqs are authored under
// the assumption that an active deal exists ("Use the link above to claim",
// "Get 100% off your first 3 months", etc.). Rendering that copy with no deal
// CTA reads as stale/broken. If/when we want richer empty-state content,
// either gate it on the deal_info or add app-level evergreen copy fields
// rather than reusing the deal-specific blocks here.
export default function DealEmptyState({ app, dealInfo }: DealEmptyStateProps) {
  const { md } = useBreakpoint()
  const { primaryColor, secondaryColor, tertiaryColor, backgroundColor } = getBrandColors(app)
  const { appIcon, appGlyph } = checkAppBranding(app)

  const gradient = `radial-gradient(70% 100% at 0% 0%, ${hexToRgba(primaryColor, 0.25)} 0%, ${hexToRgba(primaryColor, 0)} 80%),
        radial-gradient(70% 100% at 100% 0%, ${hexToRgba(secondaryColor, 0.25)} 0%, ${hexToRgba(secondaryColor, 0)} 80%),
        radial-gradient(100% 100% at 50% 0%, ${hexToRgba(tertiaryColor, 0.25)} 0%, ${hexToRgba(tertiaryColor, 0)} 80%, rgba(0, 0, 0, 0) 100%),
        #000`

  // modified = lastIsrDate (last rebuild), published = createdAt (first
  // published) — same page-level source as DealContainer.
  const pagePublished = dealInfo.createdAt
  const pageModified = dealInfo.lastIsrDate ?? dealInfo.createdAt

  return (
    <>
      <Meta
        modifiedTimestamp={pageModified}
        publishedTimestamp={pagePublished}
        parent={{ label: app.name, url: `/apps/${app.slug}` }}
      />
      <Hero
        gradient={gradient}
        ftc={true}
        animation={{ scroll: false, scale: true }}
        className="pt-[128px] md:pt-0"
      >
        {({ animations }) => (
          <Container
            className="relative flex h-full items-center justify-center md:pt-20"
            height="fill"
          >
            <Motion>
              <div className="z-20 flex w-full -translate-y-10 flex-col items-center justify-center gap-7 md:translate-y-0">
                <m.div className="relative" style={animations}>
                  {(appIcon || appGlyph) && (
                    <AppStack
                      icon={appIcon}
                      glyph={appGlyph}
                      name={app.name}
                      background={backgroundColor}
                      invert={true}
                      size={md ? 'lg' : 'sm'}
                    />
                  )}
                </m.div>

                <m.div style={animations} className="flex flex-col items-center gap-4">
                  <p
                    aria-hidden="true"
                    className="text-center font-heading text-36 font-bold md:text-64"
                  >
                    {app.name} Deal
                  </p>

                  <div className="flex items-center gap-3 text-16 text-white/60 md:text-18">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white">
                      <Icon name="circle-info" />
                    </div>
                    <p>No deal currently available</p>
                  </div>
                </m.div>
              </div>
            </Motion>
          </Container>
        )}
      </Hero>
      {/* main.overlay is required even when empty — `Hero` is absolute/fixed
          positioned and contributes no document-flow height. The `.overlay`
          class on <article> has `mt-hero` which gives the document its actual
          height (== hero height) and lets the footer sit below the hero. */}
      <article id="content" className="overlay" />
    </>
  )
}
